const UNSPLASH_API_BASE = 'https://api.unsplash.com';
const UNSPLASH_ACCESS_KEY =
  typeof import.meta.env.VITE_UNSPLASH_ACCESS_KEY === 'string'
    ? import.meta.env.VITE_UNSPLASH_ACCESS_KEY.trim()
    : '';

export interface UnsplashPhoto {
  id: string;
  width: number;
  height: number;
  alt_description: string | null;
  description: string | null;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  user: {
    name: string;
    username: string;
    profile_image?: {
      small?: string;
      medium?: string;
      large?: string;
    };
  };
  links?: {
    html?: string;
  };
}

interface UnsplashSearchResponse {
  results?: unknown;
}

export function hasUnsplashAccessKey() {
  return UNSPLASH_ACCESS_KEY.length > 0;
}

export async function searchUnsplashPhotos(
  query: string,
  page = 1,
  perPage = 20
): Promise<UnsplashPhoto[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const response = (await fetchUnsplashJson('/search/photos', {
    query: trimmedQuery,
    page,
    per_page: perPage,
    orientation: 'landscape',
    content_filter: 'high',
  })) as UnsplashSearchResponse;

  if (!Array.isArray(response.results)) {
    return [];
  }

  return response.results as UnsplashPhoto[];
}

export function getOptimizedImageUrl(
  photo: UnsplashPhoto,
  width = 1000,
  height = 700,
  fit: 'crop' | 'fill' | 'scale-down' = 'crop'
) {
  const rawUrl =
    typeof photo.urls?.raw === 'string'
      ? photo.urls.raw
      : photo.urls?.regular || photo.urls?.full || photo.urls?.small || photo.urls?.thumb;

  if (!rawUrl) {
    return '';
  }

  try {
    const url = new URL(rawUrl);
    url.searchParams.set('w', String(width));
    url.searchParams.set('h', String(height));
    url.searchParams.set('fit', fit);
    url.searchParams.set('crop', 'entropy');
    url.searchParams.set('auto', 'format');
    url.searchParams.set('q', '80');
    return url.toString();
  } catch {
    return rawUrl;
  }
}

export function generateAltText(photo: UnsplashPhoto, searchTerm?: string) {
  if (typeof photo.alt_description === 'string' && photo.alt_description.trim().length > 0) {
    return photo.alt_description.trim();
  }
  if (typeof photo.description === 'string' && photo.description.trim().length > 0) {
    return photo.description.trim();
  }
  if (searchTerm && searchTerm.trim().length > 0) {
    return `Image of ${searchTerm.trim()}`;
  }
  return `Photo by ${photo.user.name} on Unsplash`;
}

export function buildUnsplashCredit(photo: UnsplashPhoto) {
  const photographer = photo.user?.name?.trim() || 'Unknown photographer';
  return `Photo by ${photographer} on Unsplash`;
}

async function fetchUnsplashJson(path: string, params: Record<string, string | number>) {
  if (!hasUnsplashAccessKey()) {
    throw new Error(
      'Unsplash is not configured. Set VITE_UNSPLASH_ACCESS_KEY in your frontend environment.'
    );
  }

  const url = new URL(path, UNSPLASH_API_BASE);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, String(value));
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}`,
      'Accept-Version': 'v1',
    },
  });

  if (!response.ok) {
    const responseBody = await response.text().catch(() => '');
    throw new Error(`Unsplash request failed (${response.status}). ${responseBody.slice(0, 200)}`);
  }

  return response.json() as Promise<unknown>;
}
